// @flow
import * as React from 'react';
// $$FlowIssue
import { forwardRef, useEffect, useRef, useState } from 'react';
import styles from './Tabs.module.scss';

type TabsProps = {
  id: Id,
  index: number,
  onChange: number => void,
  children: React.Node,
};
export function Tabs(props: TabsProps) {
  const { id, index: selectedIndex, onChange, children } = props;
  const arr = React.Children.toArray(children);
  const tabs = arr.filter(child => child.type === Tab);
  const tabpanels = arr.filter(child => child.type === TabPanel);
  const tablistRef = useRef(null);
  const [keyDownHandled, setKeyDownHandled] = useState(null);

  if (tabs.length !== tabpanels.length) {
    throw new Error('Mismatch between <Tabs/> & <TabPanels />');
  }

  function tabId(strings: string[]) {
    return `${id}-tab-${strings.join('-')}`;
  }

  function tabPanelId(strings: string[]) {
    return `${id}-tabpanel-${strings.join('-')}`;
  }

  function handleKeyDown(e) {
    let nextSelectedIndex = selectedIndex;
    if (e.key === 'ArrowRight') {
      nextSelectedIndex += 1;
    } else if (e.key === 'ArrowLeft') {
      nextSelectedIndex -= 1;
    }

    if (nextSelectedIndex === selectedIndex) {
      return;
    }
    e.stopPropagation();
    e.preventDefault();
    if (nextSelectedIndex === tabs.length) nextSelectedIndex = 0;
    if (nextSelectedIndex < 0) nextSelectedIndex = tabs.length - 1;

    setKeyDownHandled(Date.now());
    onChange(nextSelectedIndex);
  }

  useEffect(
    () => {
      if (keyDownHandled) {
        // we only want this effect run when a keyDown event happens
        // without this guard it will run on mount, stealing focus form the modal close button
        // violating ARIA recommendations
        tablistRef.current.querySelector('[aria-selected=true]').focus();
      }
    },
    [keyDownHandled]
  );

  return (
    <React.Fragment>
      <TabList onKeyDown={handleKeyDown} ref={tablistRef}>
        {tabs.map((el, index) => {
          const isSelected = index === selectedIndex;

          return React.cloneElement(el, {
            role: 'tab',
            id: tabId`index`,
            'aria-controls': tabPanelId`index`,
            'aria-selected': isSelected,
            tabIndex: index === selectedIndex ? 0 : -1,
            onClick: e => {
              if (isSelected) return;
              onChange(index);
              el.props.onClick && tabs.props.onClick();
            },
          });
        })}
      </TabList>
      <TabPanels>
        {tabpanels.map((el, index) => {
          const isSelected = selectedIndex === index;

          return React.cloneElement(el, {
            role: 'tabpanel',
            id: tabPanelId`index`,
            'aria-labelledby': tabId`index`,
            tabIndex: 0,
            style: {
              display: isSelected ? 'block' : 'none',
            },
          });
        })}
      </TabPanels>
    </React.Fragment>
  );
}

export const TabList = forwardRef(function TabList(props: *, ref) {
  return <div {...props} className={styles.tablist} ref={ref} />;
});

export const Tab = forwardRef(function Tab(props: *, ref) {
  return <button type="button" className={styles.tab} {...props} ref={ref} />;
});

export const TabPanels = forwardRef(function TabPanels(props: *, ref) {
  return <div className={styles.tabpanels} {...props} />;
});

export const TabPanel = forwardRef(function TabPanel(props: *, ref) {
  return <div {...props} className={styles.tabpanel} ref={ref} />;
});



// WEBPACK FOOTER //
// ./src/components/ContentSection/__future__/MediaUpload/Tabs.js